import { SelectInput } from './select.mjs';
import { composedInputBuilder, create, registerCustomElement, createAddFirstAccountBtn } from '../utils.mjs';
import EVENTS from '../events.mjs';
import FormanConfig from '../formanConfig.mjs';
import RemoteForm from '../controls/remoteFormPanel.mjs';
import { I18n } from '../helpers/i18n.mjs';
import { DEFAULT_THEME } from '../helpers/color.mjs';
import EventManager from '../eventManager.mjs';

const config = FormanConfig.config.fields.datastore;

export class DatastoreInput extends SelectInput {
	constructor() {
		super();

		this.panel = null;
		this._firstCreationMode = false;
	}

	get allowApiCall() {
		return true;
	}

	get hasModeSwitch() {
		return false;
	}

	get stateDataProps() {
		return [];
	}

	extendInstructions(instructions, value, metadata) {
		instructions.options = instructions.options || {};

		instructions.options.label = instructions.options.label || config.api.list.label;
		instructions.options.value = instructions.options.value || config.api.list.value;
		instructions.options.wrapper = instructions.options.wrapper || config.api.list.wrapper;
		instructions.options.placeholder = instructions.options.placeholder || I18n.l('datastore.chooseDatastore');
		instructions.options.team = this.getTeamId(instructions);

		instructions.theme = instructions.theme || this.form.meta.module.package?.theme || DEFAULT_THEME;

		const storeQuery = new URLSearchParams();

		storeQuery.append('teamId', instructions.options.team);
		storeQuery.append('pg[limit]', config.api.list.limit);

		instructions.options.store = `${config.api.list.path}?${storeQuery}`;

		return instructions;
	}

	_shouldLoadOnBuild(options, value) {
		return typeof value !== 'undefined' && value !== '';
	}

	/**
	 * Builds the input dom.
	 *
	 * @param {object} instructions Collection of directives.
	 * @param {any} value Value.
	 * @param {object} metadata Metadata.
	 */

	async _build(instructions, value, metadata) {
		// Picker

		const picker = create('imt-picker');

		// Build the component

		metadata.build = Object.assign(metadata.build || {}, { picker });
		await super._build(instructions, value, metadata);

		// Build composed input

		this.append(
			composedInputBuilder({
				picker,
				config,
				name: this.form.meta.module?.package?.name,
				theme: instructions.theme,
				buttons: [
					...(this.showEditBtn
						? [
								{
									label: I18n.l('buttons.browse'),
									enabled: (option) => option?.value,
									action: () => {
										EventManager.dispatch(this.form, EVENTS.DATASTORE.BROWSE, {
											detail: {
												dataStoreId: this.value,
											},
										});
									},
								},
						  ]
						: []),
					...(this.showAddBtn
						? [
								{
									label: I18n.l('common.add'),
									action: this._createHandler(instructions),
								},
						  ]
						: []),
				],
				readonly: this.readonly,
				image: instructions?.image,
			}),
		);

		this._firstCreationMode = !this.hasStoreItems;

		picker.addEventListener(EVENTS.PICKER.LOAD_DONE, () => {
			this._firstCreationMode = !this.hasStoreItems;
		});

		picker.addEventListener(EVENTS.PICKER.LOAD_ERROR, () => {
			this._firstCreationMode = false;
		});

		// Show connection creation btn if options list is empty
		if (this._firstCreationMode) {
			// Create button for account creation
			picker.before(
				createAddFirstAccountBtn(
					instructions.theme,
					I18n.l('panels.datastores.create'),
					this._createHandler(instructions),
				),
			);
		}
	}

	_createHandler(instructions) {
		return async (event) => {
			if (this.panel) return;

			this.disabled = true;

			if (config?.api?.instructions?.create?.redirect) {
				window.location.href = config.api.instructions.create.redirect;
				return;
			}

			this.panel = new RemoteForm(event.target);

			this.panel.addEventListener(EVENTS.FORM.SUBMITTED, async (event) => {
				this.disabled = false;
				await this.addOption(event.detail.data.dataStore);

				if (this._firstCreationMode) {
					this.querySelector('.create-connection')?.remove();
					this._firstCreationMode = false;
				}
			});

			this.panel.addEventListener(EVENTS.PANEL.CLOSE, (event) => {
				this.disabled = false;
				this.panel = null;

				if (this._firstCreationMode) {
					const connectionCreationBtn = this.querySelector('.create-connection');

					if (connectionCreationBtn) {
						connectionCreationBtn.classList.remove('in-progress');
					}
				}
			});

			const instructionsQuery = new URLSearchParams();
			const metadata = {};

			instructionsQuery.append('teamId', instructions.options.team);

			if (instructions.options?.team != null) {
				metadata.teamId = instructions.options.team;
			}

			await this.panel.build(
				{
					name: I18n.l('panels.datastores.create'),
					src: `${config.api.instructions.create.path}?${instructionsQuery}`,
					theme: instructions.theme,
				},
				metadata,
			);

			this.panel.open();
		};
	}
}

registerCustomElement('imt-input-datastore', DatastoreInput);
